import React, { useState, useEffect } from 'react'
import {
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Skeleton,
  IconButton,
  useTheme,
  alpha,
  Stack,
  LinearProgress,
} from '@mui/material'
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Add,
  Receipt,
  Refresh,
  AttachMoney,
  MoneyOff,
  Insights,
  Savings,
  CreditCard,
  Group,
} from '@mui/icons-material'
import { PieChart, LineChart } from '@mui/x-charts'
import { useAuth } from '../../contexts/AuthContext'
import { dashboardService } from '../../services/dashboardService'
import { transactionService } from '../../services/transactionService'
import { DashboardData, ChartData, Transaction } from '../../types'
import { useNavigate } from 'react-router-dom'
import ErrorMessage from '../../components/Common/ErrorMessage'
import ResponsiveContainer from '../../components/Common/ResponsiveContainer'
import { ResponsiveGrid, ResponsiveGridItem, ResponsiveBreakpoints } from '../../components/Common/ResponsiveGrid'
import StatsCard from '../../components/Common/StatsCard'
import ResponsiveCard from '../../components/Common/ResponsiveCard'

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [chartData, setChartData] = useState<ChartData | null>(null)
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [balanceVisible, setBalanceVisible] = useState(true)

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use only the dashboard endpoint since the report endpoints don't exist yet
      const dashboardResult = await dashboardService.getDashboard()
      
      setDashboardData(dashboardResult)
      
      // Set chart data from dashboard response
      if (dashboardResult.monthlyTrends && dashboardResult.monthlyTrends.length > 0) {
        setChartData({
          monthlyTrends: dashboardResult.monthlyTrends,
          categoryBreakdown: (dashboardResult.categoryBreakdown || []).map(item => ({
            category: item.categoryName,
            amount: item.amount,
            color: item.color
          }))
        })
      }
      
      // Get recent transactions separately
      try {
        const transactionsResult = await transactionService.getTransactions(1, 5, { sortBy: 'date', sortOrder: 'desc' })
        setRecentTransactions(transactionsResult.data || [])
      } catch (transError) {
        setRecentTransactions([])
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Only fetch data if user is authenticated and token exists
    if (user && localStorage.getItem('auth_token')) {
      fetchDashboardData()
    }
  }, [user])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const getStatsData = () => {
    if (!dashboardData) return []
    
    return [
      {
        title: 'Total Balance',
        value: formatCurrency(dashboardData.currentBalance),
        icon: <AccountBalance />,
        color: dashboardData.currentBalance >= 0 ? 'success' : 'error',
        trend: dashboardData.balanceChange ? `${dashboardData.balanceChange > 0 ? '+' : ''}${dashboardData.balanceChange.toFixed(1)}%` : 'N/A',
        trendDirection: dashboardData.balanceChange && dashboardData.balanceChange > 0 ? 'up' : 'down',
        subtitle: 'Current Balance',
      },
      {
        title: 'Monthly Income',
        value: formatCurrency(dashboardData.monthlyIncome),
        icon: <TrendingUp />,
        color: 'success',
        trend: dashboardData.incomeChange ? `${dashboardData.incomeChange > 0 ? '+' : ''}${dashboardData.incomeChange.toFixed(1)}%` : 'N/A',
        trendDirection: dashboardData.incomeChange && dashboardData.incomeChange > 0 ? 'up' : 'down',
        subtitle: 'This Month',
      },
      {
        title: 'Monthly Expenses',
        value: formatCurrency(dashboardData.monthlyExpenses),
        icon: <TrendingDown />,
        color: 'error',
        trend: dashboardData.expenseChange ? `${dashboardData.expenseChange > 0 ? '+' : ''}${dashboardData.expenseChange.toFixed(1)}%` : 'N/A',
        trendDirection: dashboardData.expenseChange && dashboardData.expenseChange > 0 ? 'up' : 'down',
        subtitle: 'This Month',
      },
      {
        title: 'Savings Rate',
        value: dashboardData.monthlyIncome > 0 
          ? `${(((dashboardData.monthlyIncome - dashboardData.monthlyExpenses) / dashboardData.monthlyIncome) * 100).toFixed(1)}%`
          : '0%',
        icon: <Savings />,
        color: 'primary',
        trend: 'Target: 20%',
        trendDirection: 'neutral',
        subtitle: 'Monthly Savings',
      },
    ]
  }

  const quickActions = [
    {
      title: 'Add Transaction',
      description: 'Record income or expense',
      icon: <Add />,
      color: 'primary',
      action: () => navigate('/transactions'),
    },
    {
      title: 'View Reports',
      description: 'Detailed analytics',
      icon: <Insights />,
      color: 'secondary',
      action: () => navigate('/dashboard'),
    },
    {
      title: 'Manage Groups',
      description: 'Shared expenses',
      icon: <Group />,
      color: 'info',
      action: () => navigate('/groups'),
    },
    {
      title: 'Loan Tracker',
      description: 'EMI & payments',
      icon: <CreditCard />,
      color: 'warning',
      action: () => navigate('/loans'),
    },
  ]

  if (loading) {
    return (
      <ResponsiveContainer>
        {/* Header Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
        </Box>
        
        {/* Stats Cards Skeleton */}
        <ResponsiveGrid spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <ResponsiveGridItem key={i} {...ResponsiveBreakpoints.quarters}>
              <StatsCard
                title=""
                value=""
                loading={true}
              />
            </ResponsiveGridItem>
          ))}
        </ResponsiveGrid>
        
        {/* Charts Skeleton */}
        <ResponsiveGrid spacing={3}>
          <ResponsiveGridItem {...ResponsiveBreakpoints.twoThirds}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </ResponsiveGridItem>
          <ResponsiveGridItem {...ResponsiveBreakpoints.oneThird}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </ResponsiveGridItem>
        </ResponsiveGrid>
      </ResponsiveContainer>
    )
  }

  if (error) {
    return (
      <ResponsiveContainer>
        <ErrorMessage 
          message={error} 
          onRetry={fetchDashboardData}
        />
      </ResponsiveContainer>
    )
  }

  const statsData = getStatsData()

  return (
    <ResponsiveContainer>
      {/* Welcome Header */}
      <Paper
        sx={{
          mb: 4,
          p: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <ResponsiveGrid spacing={3} alignItems="center">
          <ResponsiveGridItem {...ResponsiveBreakpoints.twoThirds}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 64 },
                  height: { xs: 48, sm: 64 },
                  bgcolor: 'rgba(255,255,255,0.2)',
                  fontSize: { xs: '1.2rem', sm: '1.5rem' },
                  fontWeight: 'bold',
                }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  Welcome back, {user?.firstName}!
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Here's your financial overview for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>
          </ResponsiveGridItem>
          <ResponsiveGridItem {...ResponsiveBreakpoints.oneThird}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              sx={{ width: '100%' }}
            >
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate('/transactions')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
                fullWidth
              >
                Add Transaction
              </Button>
              <IconButton
                onClick={fetchDashboardData}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                  alignSelf: { xs: 'flex-start', sm: 'center' },
                }}
              >
                <Refresh />
              </IconButton>
            </Stack>
          </ResponsiveGridItem>
        </ResponsiveGrid>
      </Paper>

      {/* Stats Cards */}
      <ResponsiveGrid spacing={3} sx={{ mb: 4 }}>
        {statsData.map((stat, index) => (
          <ResponsiveGridItem key={index} {...ResponsiveBreakpoints.quarters}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              color={stat.color as any}
              trend={{
                value: stat.trend,
                direction: stat.trendDirection as any,
              }}
              hideValue={stat.title === 'Total Balance' && !balanceVisible}
              onToggleVisibility={stat.title === 'Total Balance' ? () => setBalanceVisible(!balanceVisible) : undefined}
            />
          </ResponsiveGridItem>
        ))}
      </ResponsiveGrid>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <ResponsiveCard title="Quick Actions">
        <ResponsiveGrid spacing={2}>
          {quickActions.map((action, index) => (
            <ResponsiveGridItem key={index} {...ResponsiveBreakpoints.quarters}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4],
                  },
                }}
                onClick={action.action}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: 2,
                      borderRadius: 3,
                      bgcolor: action.color === 'primary' ? alpha(theme.palette.primary.main, 0.1) :
                               action.color === 'secondary' ? alpha(theme.palette.secondary.main, 0.1) :
                               action.color === 'info' ? alpha(theme.palette.info.main, 0.1) :
                               action.color === 'warning' ? alpha(theme.palette.warning.main, 0.1) :
                               alpha(theme.palette.primary.main, 0.1),
                      color: action.color === 'primary' ? 'primary.main' :
                             action.color === 'secondary' ? 'secondary.main' :
                             action.color === 'info' ? 'info.main' :
                             action.color === 'warning' ? 'warning.main' :
                             'primary.main',
                      mb: 2,
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {action.description}
                  </Typography>
                </CardContent>
              </Card>
            </ResponsiveGridItem>
          ))}
        </ResponsiveGrid>
      </ResponsiveCard>
      </Box>

      {/* Charts and Transactions */}
      <ResponsiveGrid spacing={3}>
        {/* Monthly Trends Chart */}
        <ResponsiveGridItem {...ResponsiveBreakpoints.twoThirds}>
          <ResponsiveCard
            title="Monthly Trends"
            actions={<Chip label="Last 6 Months" variant="outlined" size="small" />}
          >
            {chartData && chartData.monthlyTrends && chartData.monthlyTrends.length > 0 ? (
              <Box sx={{ height: { xs: 250, sm: 350 }, mt: 2 }}>
                <LineChart
                  series={[
                    {
                      data: chartData.monthlyTrends.map(item => item.income),
                      label: 'Income',
                      color: theme.palette.success.main,
                    },
                    {
                      data: chartData.monthlyTrends.map(item => item.expenses),
                      label: 'Expenses',
                      color: theme.palette.error.main,
                    }
                  ]}
                  xAxis={[{
                    data: chartData.monthlyTrends.map(item => item.month),
                    scaleType: 'point',
                  }]}
                  height={350}
                  margin={{ left: 80, right: 20, top: 20, bottom: 60 }}
                />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Insights sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No data available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add some transactions to see your financial trends
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('/transactions')}
                >
                  Add First Transaction
                </Button>
              </Box>
            )}
          </ResponsiveCard>
        </ResponsiveGridItem>

        {/* Right Column */}
        <ResponsiveGridItem {...ResponsiveBreakpoints.oneThird}>
          <Stack spacing={3}>
            {/* Recent Transactions */}
            <ResponsiveCard
              title="Recent Transactions"
              actions={
                <Button
                  size="small"
                  onClick={() => navigate('/transactions')}
                  sx={{ textTransform: 'none' }}
                >
                  View All
                </Button>
              }
            >
              {recentTransactions.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {recentTransactions.map((transaction, index) => (
                    <React.Fragment key={transaction.id}>
                      <ListItem sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 2,
                              bgcolor: transaction.type === 'income' 
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.error.main, 0.1),
                              color: transaction.type === 'income' ? 'success.main' : 'error.main',
                            }}
                          >
                            {transaction.type === 'income' ? <AttachMoney /> : <MoneyOff />}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {transaction.description}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {transaction.categoryName} • {new Date(transaction.date).toLocaleDateString()}
                            </Typography>
                          }
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: transaction.type === 'income' ? 'success.main' : 'error.main',
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          }}
                        >
                          {transaction.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                        </Typography>
                      </ListItem>
                      {index < recentTransactions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No transactions yet
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => navigate('/transactions')}
                  >
                    Add Transaction
                  </Button>
                </Box>
              )}
            </ResponsiveCard>

            {/* Category Breakdown */}
            {chartData && chartData.categoryBreakdown && chartData.categoryBreakdown.length > 0 && (
              <ResponsiveCard title="Expense Categories">
                <Box sx={{ height: 200, mb: 2 }}>
                  <PieChart
                    series={[{
                      data: chartData.categoryBreakdown.map((item, index) => ({
                        id: index,
                        value: item.amount,
                        label: item.category,
                      })),
                      highlightScope: { faded: 'global', highlighted: 'item' },
                    }]}
                    height={200}
                  />
                </Box>
              </ResponsiveCard>
            )}

            {/* Financial Health Score */}
            <ResponsiveCard title="Financial Health">
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Overall Score</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    85/100
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={85}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: theme.palette.success.main,
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Great job! You're maintaining healthy spending habits and building savings consistently.
              </Typography>
            </ResponsiveCard>
          </Stack>
        </ResponsiveGridItem>
      </ResponsiveGrid>
    </ResponsiveContainer>
  )
}

export default DashboardPage